import classNames from 'classnames';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import Button from '../../components/Button';
import OverflowMenu from '../../components/OverflowMenu';
import SVGIcon from '../../components/SVGIcon';
import Tooltip from '../../components/Tooltip';
import overflowMenuIcon from '../../icons/overflowMenu.svg';
import {
  PostReorganizationMetadataShape,
  TopicReorganizationMetadataShape,
} from '../useTopicReorganization';
import styles from './TopicTreeCardItem.module.scss';
import { MoveStates, PostStatus, TopicStatus } from '../constants';

const getTooltipForStatus = (type, status) => {
  switch (status) {
    case TopicStatus.deleted:
    case PostStatus.deleted:
      return `This ${type} will be deleted`;
    case PostStatus.published:
      return `This ${type} will be published`;
    case PostStatus.unpublished:
      return `This ${type} will be unpublished`;
    case PostStatus.archived:
      return `This ${type} will be archived`;
    case PostStatus.unarchived:
      return `This ${type} will be unarchived`;
    default:
      return null;
  }
};

const getTooltips = ({
  reorganizationMetadata,
  inheritedMove,
  type,
  topicOriginalName,
}) => {
  const { status, isNew, name, isPinned } = reorganizationMetadata;

  const tooltips = [];
  if (inheritedMove.state !== MoveStates.movedOut) {
    if (isNew) {
      tooltips.push(`This ${type} will be added`);
    } else if (name) {
      tooltips.push(`This ${type} will be renamed from “${topicOriginalName}”`);
    }

    const statusTooltip = getTooltipForStatus(type, status);
    if (statusTooltip) {
      tooltips.push(statusTooltip);
    }

    if (typeof isPinned === 'boolean') {
      tooltips.push(`This ${type} will be ${isPinned ? 'pinned' : 'unpinned'}`);
    }
  }

  if (!isNew && inheritedMove.state === MoveStates.movedIn) {
    let targetName = type;
    if (
      type === 'topic' &&
      reorganizationMetadata.move.state !== MoveStates.movedIn
    ) {
      targetName = 'subtopic';
    }
    tooltips.push(
      `This ${targetName} will be moved from ${inheritedMove.source.name}`,
    );
  }

  return tooltips;
};

const propTypes = {
  children: PropTypes.node,
  type: PropTypes.oneOf(['post', 'topic']).isRequired,
  titleLine: PropTypes.node.isRequired,
  controls: PropTypes.node,
  menu: PropTypes.node.isRequired,
  reorganizationMetadata: PropTypes.oneOfType([
    TopicReorganizationMetadataShape,
    PostReorganizationMetadataShape,
  ]).isRequired,
  ancestorsReorganizationMetadata: PropTypes.arrayOf(
    TopicReorganizationMetadataShape,
  ).isRequired,
  originalName: PropTypes.string,
};

const defaultProps = {
  children: null,
  controls: null,
  originalName: null,
};

const TopicTreeCardItem = ({
  children,
  type,
  titleLine,
  controls,
  menu,
  reorganizationMetadata,
  ancestorsReorganizationMetadata,
  originalName,
}) => {
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { status, isNew, name, isPinned } = reorganizationMetadata;

  const { move: inheritedMove } = _.findLast(
    [...ancestorsReorganizationMetadata.slice(1), reorganizationMetadata],
    (metadata) => metadata.move.state !== MoveStates.initial,
  ) || {
    move: { state: MoveStates.initial },
  };

  const tooltips = getTooltips({
    reorganizationMetadata,
    inheritedMove,
    type,
    topicOriginalName: originalName,
  });

  return (
    <div
      className={classNames(styles.container, styles[type], {
        [styles.movedIn]:
          !isNew &&
          inheritedMove.state === MoveStates.movedIn &&
          (type === 'post' || ancestorsReorganizationMetadata.length > 0),
      })}
    >
      <div
        onMouseEnter={() => setIsMenuVisible(true)}
        onMouseLeave={() => setIsMenuVisible(false)}
      >
        <div
          className={classNames(styles.titleLine, {
            ...(inheritedMove.state !== MoveStates.movedOut
              ? {
                  [styles.created]: isNew,
                  [styles[status]]: !isNew,
                  [styles.pinned]: !isNew && typeof isPinned === 'boolean',
                  [styles.renamed]: !isNew && name,
                }
              : {}),
          })}
        >
          <div className={styles.title}>
            {tooltips.length > 0 ? (
              <Tooltip
                tooltip={tooltips.join('\n')}
                placement="top"
                trigger="hover"
                display="block"
                popOverPortal={document.body}
              >
                {titleLine}
              </Tooltip>
            ) : (
              <>{titleLine}</>
            )}
            {menu && (
              <div
                className={classNames(styles.menu, {
                  [styles.visible]: isMenuVisible,
                  [styles.open]: isMenuOpen,
                })}
              >
                <OverflowMenu
                  open={isMenuOpen}
                  onOpenChange={setIsMenuOpen}
                  inline={false}
                  button={
                    <Button className={styles.button}>
                      <SVGIcon size={14} coloringWith="fill">
                        {overflowMenuIcon}
                      </SVGIcon>
                    </Button>
                  }
                >
                  {menu}
                </OverflowMenu>
              </div>
            )}
          </div>
          {controls && <div className={styles.controls}>{controls}</div>}
        </div>
      </div>
      {children}
    </div>
  );
};

TopicTreeCardItem.propTypes = propTypes;
TopicTreeCardItem.defaultProps = defaultProps;

export default TopicTreeCardItem;
